import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeHref } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import Flex from '@wix/da-ds/pkg/Flex';
import s from './AttachmentCounter.scss';

interface Props {
  attachmentsCount: number;
  maxAttachmentsAllowed: number;
}

const AttachmentCounter: React.FC<Props> = ({ attachmentsCount, maxAttachmentsAllowed }) => {
  const isMultiVideoFlagEnabled = useFlag('multi_video');
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const attachmentCountLabels = {
    current: attachmentsCount + 1,
    max: maxAttachmentsAllowed + 1,
  };
  let tooltipKey;
  if (isMultiVideoFlagEnabled) {
    tooltipKey = hasCoreMembership
      ? 'deviationSubmit.form.manageMedia.tooltip.core'
      : 'deviationSubmit.form.manageMedia.tooltip.nonCore';
  } else {
    tooltipKey = hasCoreMembership
      ? 'deviationSubmit.form.manageImages.tooltip.core'
      : 'deviationSubmit.form.manageImages.tooltip.nonCore';
  }

  return (
    <Flex direction="row" alignItems="center" gap={6}>
      <span className={s['attachment-count']}>
        {isMultiVideoFlagEnabled
          ? t('deviationSubmit.form.manageMedia.attachmentCount', attachmentCountLabels)
          : t('deviationSubmit.form.manageImages.attachmentCount', attachmentCountLabels)}
      </span>
      <SimpleTooltip
        aria-label={t('common.learnMore')}
        text={t(tooltipKey, {
          postProcess: 'reactPostprocessor',
          learnMoreLink: (
            <PlainButtonOrLink
              href={sanitizeHref(
                isMultiVideoFlagEnabled
                  ? t('deviationSubmit.form.manageMedia.tooltip.link')
                  : t('deviationSubmit.form.manageImages.tooltip.link')
              )}
              target="_blank"
              className={s['learn-more']}
            >
              {t('common.learnMore')}
            </PlainButtonOrLink>
          ),
        })}
        usePortal
      >
        <InfoIcon size={IconSize.SMALLER} disableDefaultColors />
      </SimpleTooltip>
    </Flex>
  );
};
AttachmentCounter.displayName = 'AttachmentCounter';

export default AttachmentCounter;
